import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { TrendingUp, Target, Users, Trophy, AlertCircle, CheckCircle } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from 'recharts';
import { analyticsService } from './analyticsService';
import { BenchmarkComparison } from './types';
import { cn } from '@/shared/lib/utils';

export const ComparativeAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const [benchmarks, setBenchmarks] = useState<BenchmarkComparison[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await analyticsService.fetchBenchmarks();
      setBenchmarks(data);
    } catch (error) {
      console.error('Failed to fetch comparative analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // Prepare radar chart data
  const radarData = benchmarks.map((b) => ({
    metric: b.metric.split(' ')[0],
    you: normalizeValue(b.yourValue, b.nationalAvg, b.top10Avg),
    regional: normalizeValue(b.regionalAvg, b.nationalAvg, b.top10Avg),
    national: normalizeValue(b.nationalAvg, b.nationalAvg, b.top10Avg),
    top10: normalizeValue(b.top10Avg, b.nationalAvg, b.top10Avg),
  }));

  function normalizeValue(value: number, baseline: number, max: number) {
    // Normalize to 0-100 scale
    return ((value - baseline) / (max - baseline)) * 100 + 50;
  }

  const getComparisonColor = (yourValue: number, avgValue: number) => {
    if (yourValue > avgValue * 1.2) return 'text-green-600';
    if (yourValue > avgValue) return 'text-green-500';
    if (yourValue > avgValue * 0.8) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getGapPercentage = (yourValue: number, targetValue: number) => {
    return ((yourValue - targetValue) / targetValue) * 100;
  };

  return (
    <div className="space-y-6">
      {/* Performance Overview */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.compare.title', 'Performance Comparison')}</CardTitle>
          <CardDescription>{t('analytics.compare.desc', 'See how you stack up against other farmers')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis dataKey="metric" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <PolarRadiusAxis angle={90} domain={[0, 150]} stroke="hsl(var(--muted-foreground))" fontSize={10} />
              <Radar name={t('analytics.compare.you', 'You')} dataKey="you" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.4} strokeWidth={2} />
              <Radar name={t('analytics.compare.regionalAvg', 'Regional Avg')} dataKey="regional" stroke="hsl(142 76% 36%)" fill="hsl(142 76% 36%)" fillOpacity={0.2} />
              <Radar name={t('analytics.compare.top10', 'Top 10%')} dataKey="top10" stroke="hsl(45 93% 47%)" fill="hsl(45 93% 47%)" fillOpacity={0.2} />
              <Legend />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Detailed Benchmarks */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.compare.benchmarks', 'Detailed Benchmarks')}</CardTitle>
          <CardDescription>{t('analytics.compare.benchmarksDesc', 'Compare your metrics across different levels')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {benchmarks.map((benchmark, index) => {
              const vsRegional = getGapPercentage(benchmark.yourValue, benchmark.regionalAvg);
              const vsTop10 = getGapPercentage(benchmark.yourValue, benchmark.top10Avg);
              const isAboveRegional = benchmark.yourValue > benchmark.regionalAvg;

              return (
                <div key={index} className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-foreground">{benchmark.metric}</h4>
                    {isAboveRegional ? (
                      <Badge variant="default" className="gap-1">
                        <CheckCircle className="h-3 w-3" />
                        {t('analytics.compare.aboveAvg', 'Above Average')}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {t('analytics.compare.belowAvg', 'Below Average')}
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div className="text-center p-2 rounded bg-primary/10">
                      <div className="text-xs text-muted-foreground mb-1">{t('analytics.compare.you', 'You')}</div>
                      <div className="text-lg font-bold text-primary">
                        {benchmark.yourValue}
                        <span className="text-xs ml-1">{benchmark.unit}</span>
                      </div>
                    </div>
                    <div className="text-center p-2 rounded bg-muted/50">
                      <div className="text-xs text-muted-foreground mb-1">{t('analytics.compare.regional', 'Regional')}</div>
                      <div className="text-lg font-bold text-foreground">
                        {benchmark.regionalAvg}
                        <span className="text-xs ml-1">{benchmark.unit}</span>
                      </div>
                    </div>
                    <div className="text-center p-2 rounded bg-muted/50">
                      <div className="text-xs text-muted-foreground mb-1">{t('analytics.compare.national', 'National')}</div>
                      <div className="text-lg font-bold text-foreground">
                        {benchmark.nationalAvg}
                        <span className="text-xs ml-1">{benchmark.unit}</span>
                      </div>
                    </div>
                    <div className="text-center p-2 rounded bg-yellow-500/10">
                      <div className="text-xs text-muted-foreground mb-1">{t('analytics.compare.top10', 'Top 10%')}</div>
                      <div className="text-lg font-bold text-yellow-600">
                        {benchmark.top10Avg}
                        <span className="text-xs ml-1">{benchmark.unit}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span
                      className={cn(
                        'font-medium',
                        getComparisonColor(benchmark.yourValue, benchmark.regionalAvg)
                      )}
                    >
                      {vsRegional > 0 ? '+' : ''}
                      {vsRegional.toFixed(1)}% vs {t('analytics.compare.regional', 'Regional')}
                    </span>
                    <span
                      className={cn(
                        'font-medium',
                        getComparisonColor(benchmark.yourValue, benchmark.top10Avg)
                      )}
                    >
                      {vsTop10 > 0 ? '+' : ''}
                      {vsTop10.toFixed(1)}% vs {t('analytics.compare.top10', 'Top 10%')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Gap Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            {t('analytics.compare.gapAnalysis', 'Gap Analysis')}
          </CardTitle>
          <CardDescription>{t('analytics.compare.gapDesc', 'What it takes to reach the top 10%')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {benchmarks
              .filter((b) => b.yourValue < b.top10Avg)
              .map((benchmark, index) => {
                const gap = benchmark.top10Avg - benchmark.yourValue;
                const gapPercentage = (gap / benchmark.top10Avg) * 100;

                return (
                  <div key={index} className="p-4 rounded-lg border bg-card">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm text-foreground">{benchmark.metric}</h4>
                      <Badge variant="destructive">
                        -{gap.toFixed(1)} {benchmark.unit}
                      </Badge>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3 mb-2">
                      <div
                        className="bg-primary h-3 rounded-full"
                        style={{ width: `${100 - gapPercentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      You need {gap.toFixed(1)} more {benchmark.unit} to reach top 10%
                      ({gapPercentage.toFixed(0)}% gap)
                    </p>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>

      {/* Improvement Plan */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            {t('analytics.compare.improvementPlan', 'Personalized Improvement Plan')}
          </CardTitle>
          <CardDescription>{t('analytics.compare.improvementDesc', 'Recommended actions to reach top performer status')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {benchmarks
              .filter((b) => b.yourValue < b.top10Avg)
              .sort((a, b) => (b.top10Avg - b.yourValue) - (a.top10Avg - a.yourValue))
              .slice(0, 3)
              .map((benchmark, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <Badge variant="secondary" className="mb-2">Priority {index + 1}</Badge>
                      <h4 className="font-semibold text-sm text-foreground">
                        Improve {benchmark.metric}
                      </h4>
                    </div>
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Current: {benchmark.yourValue} {benchmark.unit} | Target: {benchmark.top10Avg}{' '}
                    {benchmark.unit}
                  </p>
                  <Button size="sm" variant="outline" className="w-full">
                    {t('analytics.compare.viewPlan', 'View Action Plan')}
                  </Button>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
